import { Box, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import { isNotNullOrUndefined } from '@weft/utils';
import { useValidateMeasurementData } from 'apps/app/src/hooks/requests/use-validate-measurement-data';
import { useProgress } from 'apps/app/src/hooks/use-progress';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { HandleDialogSubmitParams } from './UploadMenuItem';

type DataMeteringFilePreviewProps = {
  file: File;
  handleDelete: () => void;
  setValid: (isValid: boolean) => void;
};

export const DataMeteringFilePreview: React.FC<DataMeteringFilePreviewProps> = ({ file, handleDelete, setValid }) => {
  const { setValue } = useFormContext<Partial<HandleDialogSubmitParams>, unknown, HandleDialogSubmitParams>();
  const { data, isLoading, isError, error } = useValidateMeasurementData(file);
  const progress = useProgress();
  const fileSize = bytesToMegaBytes(file.size);

  const getsStatus = () => {
    if (isLoading) {
      return 'Loading';
    }
    if (isError) {
      return 'Failed';
    }

    return 'Success';
  };
  
  useEffect(() => {
    if (isNotNullOrUndefined(data)) {
      setValue('customerNames', data.details);
      setValid(true);
    }
  }, [data]);

  return (
    <>
      <Stack 
        direction="row"
        spacing={2}
        alignItems="center"
        padding={1}
      >
        <Stack sx={{ width: '40px', height: '60px', alignItems: 'center', justifyContent: 'center' }}>
          <VyIcon
            icon="UploadFileFilled"
            color='#3A78F8'
          />
        </Stack>
        <Stack
          direction="column"
          spacing={0.5}
          sx={{ width: '540px' }}
        >
          <Typography
            variant="body1"
            color="text.primary"
            fontSize={16}
          >
            {file.name}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ wordSpacing: '2px' }}
          >
            {`${fileSize}kb • `} 
            {getsStatus()}
          </Typography>

          {isLoading && (
            <LinearProgress
              variant="determinate"
              sx={{ borderRadius: '4px', height: '4px', width: '200px', gap: '8px' }}
              value={progress}
            />
          )}

          {isNotNullOrUndefined(data) && isNotNullOrUndefined(data.message) && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {data.message}
            </Typography>
          )}
        </Stack>
        <Box
          display='flex'
          alignItems='center'
        >
          <IconButton
            size="small"
            onClick={handleDelete}
          >
            <VyIcon icon="Delete" />
          </IconButton>

          {isNotNullOrUndefined(data) && (
            <VyIcon
              icon="CheckCircle"
              fill='#00E676'
            />
          )}
        </Box>
      </Stack>
      {isError && (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {isErrorWithMessage(error) && (
            <pre>
              <Typography
                variant="body2"
                color="error"
                sx={{ padding: '10px', marginLeft: '40px' }}
              >
                {error.message}
              </Typography>
            </pre>
          )}
        </div>
      )}
    </>
  );
};

function bytesToMegaBytes(bytes: number) { 
  return (bytes / (1024)).toFixed(); 
}

function isErrorWithMessage(error: unknown): error is Error {
  return error instanceof Error;
}